import { Box, Text } from '@primer/react';
import Reveal from '../components/Reveal';
import ServiceCTA from '../components/ServiceCTA';
import SEO from '../components/SEO';
import HeroBackground from '../components/HeroBackground';
import { SITE_URL } from '../utils/seo';
import aboutHeroVideo from '../assets/about-hero-video.webm';
import aboutHeroPoster from '../assets/posters/about-hero-video-poster.jpg';
import founderPhoto from '../assets/bout1.jpeg';

function About() {
  return (
    <Box sx={{ backgroundColor: '#05070a' }}>
      <SEO
        title="About Pegasus UX | Dallas UX Design Agency"
        description="Pegasus UX is a Dallas, TX UX design agency partnering with enterprise, fintech, and AI teams, including GitHub, McKinsey, Verizon, and State Farm, to design complex products that feel simple."
        keywords="dallas ux design agency, ux agency dallas tx, ui design consultants, best ux companies, best ux design agencies, ui ux design company, ux consulting companies, enterprise UX design agency, fintech UX design partners, AI product design experience, GitHub Copilot UX design, McKinsey UX consulting"
        path="/about"
        image={`${SITE_URL}/og/about.jpg`}
      />
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          paddingX: ['24px', '48px', '100px'],
          paddingBottom: '10vh',
          overflow: 'hidden',
          backgroundColor: '#05070a',
        }}
      >
        <HeroBackground videoSrc={aboutHeroVideo} poster={aboutHeroPoster} />

        <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(5, 7, 10, 0.6)' }} />

        <Reveal sx={{ position: 'relative' }}>
          <Text
            as="h1"
            sx={{
              margin: 0,
              marginBottom: 5,
              fontFamily: "'Fraunces', serif",
              fontWeight: 300,
              fontSize: ['2.25rem', '3rem', '3.75rem'],
              color: '#f5f1ea',
            }}
          >
            About Us
          </Text>
        </Reveal>

        <Reveal delay={0.15} sx={{ position: 'relative' }}>
          <Text
            as="p"
            sx={{
              margin: 0,
              maxWidth: '760px',
              fontSize: 3,
              lineHeight: 1.8,
              color: 'rgba(255, 255, 255, 0.6)',
            }}
          >
            Pegasus UX is a Dallas, TX-based, remote-first UX agency built
            for enterprise, fintech, AI, developer experience, logistics, and
            SaaS teams. We're headquartered in Dallas, and as a remote-first
            team, we partner with product teams across the country. We've
            partnered with{' '}
            <Text as="span" sx={{ fontWeight: 700, color: '#f5f1ea' }}>
              GitHub's
            </Text>{' '}
            Enterprise and Customer Success teams to improve support
            experiences and help incorporate{' '}
            <Text as="span" sx={{ fontWeight: 700, color: '#f5f1ea' }}>
              Copilot AI
            </Text>{' '}
            into their product. We've worked alongside{' '}
            <Text as="span" sx={{ fontWeight: 700, color: '#f5f1ea' }}>
              McKinsey &amp; Company
            </Text>{' '}
            on strategic engagements, and partnered with{' '}
            <Text as="span" sx={{ fontWeight: 700, color: '#f5f1ea' }}>
              Constellation Energy
            </Text>{' '}
            and{' '}
            <Text as="span" sx={{ fontWeight: 700, color: '#f5f1ea' }}>
              Verizon
            </Text>{' '}
            on sales platforms, CRMs, and server-tracking applications. We've
            also designed the customer and agent experience for{' '}
            <Text as="span" sx={{ fontWeight: 700, color: '#f5f1ea' }}>
              State Farm's
            </Text>{' '}
            financial technology platforms. We're looking forward to working
            with you.
          </Text>
        </Reveal>
      </Box>

      <Box
        sx={{
          paddingX: ['24px', '48px', '100px'],
          paddingY: ['64px', '80px', '120px'],
          backgroundColor: '#05070a',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: ['column', 'column', 'row'],
            alignItems: 'center',
            gap: [5, 5, 7],
            maxWidth: '1100px',
            margin: '0 auto',
          }}
        >
          <Reveal sx={{ width: ['100%', '340px', '380px'], flex: '0 0 auto' }}>
            <Box
              as="img"
              src={founderPhoto}
              alt="Joshua Bracken, founder of Pegasus UX"
              sx={{
                display: 'block',
                width: '100%',
                aspectRatio: '4 / 5',
                objectFit: 'cover',
                objectPosition: 'center 20%',
                borderRadius: '12px',
              }}
            />
          </Reveal>

          <Reveal delay={0.1} sx={{ flex: 1, textAlign: ['center', 'center', 'left'] }}>
            <Text
              as="h2"
              sx={{
                margin: 0,
                marginBottom: 4,
                fontFamily: "'Fraunces', serif",
                fontWeight: 300,
                fontSize: ['1.75rem', '2rem', '2.25rem'],
                color: '#f5f1ea',
              }}
            >
              The person behind it all
            </Text>
            <Text
              as="p"
              sx={{
                margin: 0,
                fontSize: 2,
                lineHeight: 1.8,
                color: 'rgba(255, 255, 255, 0.6)',
              }}
            >
              Hi, I'm Joshua Bracken. I started Pegasus UX because I love
              turning hard, messy problems into products that feel easy to
              use. I've spent my career working with teams at places like
              GitHub, McKinsey, and State Farm, helping them build software
              that people actually enjoy. When you work with Pegasus UX,
              you're working with me. No hand offs, no middlemen. Just good
              design, done right.
            </Text>
          </Reveal>
        </Box>
      </Box>

      <ServiceCTA
        eyebrow="Let's build something great."
        headline={[
          { text: 'Ready to start', tone: 'plain' },
          { text: 'your next project?', tone: 'gradient' },
        ]}
        subtitle="Schedule a consultation and let's talk about how Pegasus UX can help your team ship better products."
      />
    </Box>
  );
}

export default About;
